<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import {
  NSpace,
  NButton,
  NSwitch,
  NText,
  NInput,
  NTimePicker,
  NCheckboxGroup,
  NCheckbox,
  NCollapse,
  NCollapseItem,
} from 'naive-ui';
import { getTimeUntilAlarm, getDayName, parseTimeToMinutes } from '@awesomeclocks/utils';
import type { AlarmWidgetConfig } from '@awesomeclocks/types';
import WidgetWrapper from './WidgetWrapper.vue';

interface Props {
  widget: AlarmWidgetConfig;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  remove: [];
  updateName: [name: string];
  updateSettings: [settings: AlarmWidgetConfig['settings']];
}>();

const showAddForm = ref(false);
const newAlarmTime = ref<number | null>(null);
const newAlarmLabel = ref('');
const newAlarmDays = ref<number[]>([]);
const lastTriggeredKey = ref<Record<string, string>>({});

const currentTime = ref(new Date());
let intervalId: number | null = null;

const dayOptions = Array.from({ length: 7 }, (_, i) => ({
  label: getDayName(i, true),
  value: i,
}));

const updateTime = () => {
  currentTime.value = new Date();
  checkAlarms();
};

const checkAlarms = () => {
  const current = currentTime.value;
  const currentDay = current.getDay();
  const currentMinutes = current.getHours() * 60 + current.getMinutes();
  const triggerKey = `${current.toDateString()}-${String(currentMinutes).padStart(4, '0')}`;
  const allDays = [0, 1, 2, 3, 4, 5, 6];

  props.widget.settings.alarms.forEach((alarm) => {
    const alarmDays = alarm.days.length > 0 ? alarm.days : allDays;
    const lastKey = lastTriggeredKey.value[alarm.id];
    if (
      alarm.enabled &&
      alarmDays.includes(currentDay) &&
      Math.abs(parseTimeToMinutes(alarm.time) - currentMinutes) < 1 &&
      lastKey !== triggerKey
    ) {
      triggerAlarm(alarm.label);
      lastTriggeredKey.value = { ...lastTriggeredKey.value, [alarm.id]: triggerKey };
    }
  });
};

const triggerAlarm = (label: string) => {
  // Play sound and show notification
  try {
    const AudioContextCtor =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (AudioContextCtor) {
      const context = new AudioContextCtor();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = 880;
      gain.gain.value = 0.12;
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 1);
      oscillator.onended = () => context.close();
    }
  } catch {
    // Ignore audio errors to avoid blocking the alarm flow.
  }

  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('Alarm', {
      body: label || 'Alarm triggered!',
    });
  }
};

onMounted(() => {
  intervalId = window.setInterval(updateTime, 1000);
  // Request notification permission
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
});

onUnmounted(() => {
  if (intervalId !== null) {
    clearInterval(intervalId);
  }
});

const addAlarm = () => {
  if (newAlarmTime.value !== null) {
    const timeDate = new Date(newAlarmTime.value);
    const hours = timeDate.getHours();
    const minutes = timeDate.getMinutes();
    const timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    const days =
      newAlarmDays.value.length > 0 ? [...newAlarmDays.value] : [0, 1, 2, 3, 4, 5, 6];

    const alarms = [
      ...props.widget.settings.alarms,
      {
        id: `alarm-${Date.now()}`,
        time: timeString,
        days,
        enabled: true,
        label: newAlarmLabel.value || 'Alarm',
      },
    ];

    emit('updateSettings', { ...props.widget.settings, alarms });

    // Reset form
    newAlarmTime.value = null;
    newAlarmLabel.value = '';
    newAlarmDays.value = [];
    showAddForm.value = false;
  }
};

const removeAlarm = (id: string) => {
  const alarms = props.widget.settings.alarms.filter((a) => a.id !== id);
  emit('updateSettings', { ...props.widget.settings, alarms });
};

const toggleAlarm = (id: string, enabled: boolean) => {
  const alarms = props.widget.settings.alarms.map((a) =>
    a.id === id ? { ...a, enabled } : a
  );
  emit('updateSettings', { ...props.widget.settings, alarms });
};

const getNextAlarmText = (alarm: (typeof props.widget.settings.alarms)[0]) => {
  if (!alarm.enabled) return 'Disabled';
  return getTimeUntilAlarm(alarm.time, alarm.days);
};

const getDaysText = (days: number[]) => {
  if (days.length === 0) return 'Every day';
  if (days.length === 7) return 'Every day';
  if (days.length === 5 && !days.includes(0) && !days.includes(6)) return 'Weekdays';
  if (days.length === 2 && days.includes(0) && days.includes(6)) return 'Weekends';
  return days
    .sort()
    .map((d) => getDayName(d, true))
    .join(', ');
};
</script>

<template>
  <WidgetWrapper
    :widget="widget"
    @remove="emit('remove')"
    @update-name="emit('updateName', $event)"
  >
    <NSpace vertical :size="16" style="width: 100%">
      <!-- Alarms List -->
      <div class="alarms-list">
        <NCollapse v-if="widget.settings.alarms.length > 0">
          <NCollapseItem
            v-for="alarm in widget.settings.alarms"
            :key="alarm.id"
            :title="alarm.label"
          >
            <template #header>
              <NSpace justify="space-between" align="center" style="width: 100%">
                <div class="alarm-header">
                  <NText strong>{{ alarm.time }}</NText>
                  <NText :depth="3" style="font-size: 0.85rem">
                    {{ getDaysText(alarm.days) }}
                  </NText>
                </div>
                <NSwitch
                  :value="alarm.enabled"
                  @update:value="(val) => toggleAlarm(alarm.id, val)"
                  @click.stop
                />
              </NSpace>
            </template>

            <NSpace vertical :size="8">
              <NSpace align="center" justify="space-between">
                <NText>Label:</NText>
                <NText>{{ alarm.label }}</NText>
              </NSpace>

              <NSpace align="center" justify="space-between">
                <NText>Time:</NText>
                <NText>{{ alarm.time }}</NText>
              </NSpace>

              <NSpace align="center" justify="space-between">
                <NText>Days:</NText>
                <NText>{{ getDaysText(alarm.days) }}</NText>
              </NSpace>

              <NSpace align="center" justify="space-between">
                <NText>Next:</NText>
                <NText :depth="2">{{ getNextAlarmText(alarm) }}</NText>
              </NSpace>

              <NButton size="small" type="error" block @click="removeAlarm(alarm.id)">
                Delete Alarm
              </NButton>
            </NSpace>
          </NCollapseItem>
        </NCollapse>

        <div v-else class="empty-state">
          <NText :depth="3">No alarms set. Click below to add one.</NText>
        </div>
      </div>

      <!-- Add Alarm Form -->
      <div v-if="showAddForm" class="add-form">
        <NSpace vertical :size="12">
          <div>
            <NText>Time</NText>
            <NTimePicker
              v-model:value="newAlarmTime"
              format="HH:mm"
              :actions="null"
              style="width: 100%; margin-top: 4px"
            />
          </div>

          <div>
            <NText>Label</NText>
            <NInput
              v-model:value="newAlarmLabel"
              placeholder="e.g., Wake up"
              style="margin-top: 4px"
            />
          </div>

          <div>
            <NText>Repeat on</NText>
            <NCheckboxGroup v-model:value="newAlarmDays" style="margin-top: 8px">
              <NSpace>
                <NCheckbox v-for="day in dayOptions" :key="day.value" :value="day.value">
                  {{ day.label }}
                </NCheckbox>
              </NSpace>
            </NCheckboxGroup>
          </div>

          <NSpace>
            <NButton
              size="small"
              type="primary"
              :disabled="newAlarmTime === null"
              @click="addAlarm"
            >
              Add
            </NButton>
            <NButton size="small" @click="showAddForm = false">Cancel</NButton>
          </NSpace>
        </NSpace>
      </div>

      <NButton v-else size="small" block @click="showAddForm = true">
        + Add Alarm
      </NButton>
    </NSpace>
  </WidgetWrapper>
</template>

<style scoped>
.alarms-list {
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
}

.alarm-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.empty-state {
  padding: 32px;
  text-align: center;
}

.add-form {
  padding: 16px;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}
</style>
